import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { translations, loadEnDefaults, type TranslationKey } from "@/lib/translations";
import { publicRows } from "@/lib/publicBackend";

export type Locale = "de" | "en";

interface UiTranslationRow {
  key: string;
  de: string;
  en: string | null;
  source: string;
  fingerprint_de: string | null;
}

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
  t: (key: TranslationKey) => string;
  /** Pick localized field from a record using `${field}_en` convention. */
  pickLocalized: <T extends Record<string, unknown>>(record: T | null | undefined, field: string) => string | undefined;
  isLoading: boolean;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: "de",
  setLocale: () => {},
  toggleLocale: () => {},
  t: (key) => translations[key]?.de ?? key,
  pickLocalized: () => undefined,
  isLoading: false,
});

export const useLocale = () => useContext(LocaleContext);

function readInitialLocale(): Locale {
  if (typeof window === "undefined") return "de";
  const url = new URLSearchParams(window.location.search).get("lang");
  if (url === "en" || url === "de") return url;
  const stored = window.localStorage.getItem("locale");
  if (stored === "en" || stored === "de") return stored;
  return "de";
}

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(readInitialLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem("locale", next);
      const url = new URL(window.location.href);
      if (next === "de") url.searchParams.delete("lang");
      else url.searchParams.set("lang", next);
      window.history.replaceState({}, "", url.toString());
      document.documentElement.lang = next;
    } catch {
      /* ignore */
    }
  }, []);

  const toggleLocale = useCallback(() => {
    setLocale(locale === "de" ? "en" : "de");
  }, [locale, setLocale]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  // Load DB overrides for EN
  const { data: overrides, isLoading } = useQuery({
    queryKey: ["ui-translations"],
    enabled: locale === "en",
    queryFn: async () => {
      const data = await publicRows<UiTranslationRow>(
        "ui_translations",
        "select=key,de,en,source,fingerprint_de",
      );
      const map = new Map<string, UiTranslationRow>();
      data.forEach((row) => map.set(row.key, row));
      return map;
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Englische Defaults erst laden, wenn EN aktiv ist
  const [enDefaults, setEnDefaults] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    if (locale !== "en" || enDefaults) return;
    let active = true;
    loadEnDefaults().then((m) => { if (active) setEnDefaults(m); }).catch(() => {});
    return () => { active = false; };
  }, [locale, enDefaults]);

  const t = useCallback(
    (key: TranslationKey) => {
      const entry = translations[key];
      const de = entry?.de ?? key;
      if (locale === "de") return de;
      const dbEn = overrides?.get(key)?.en;
      if (dbEn && dbEn.trim()) return dbEn;
      return enDefaults?.[key] ?? de;
    },
    [locale, overrides, enDefaults],
  );

  const pickLocalized = useCallback(
    <T extends Record<string, unknown>>(record: T | null | undefined, field: string) => {
      if (!record) return undefined;
      if (locale === "en") {
        const enVal = record[`${field}_en`];
        if (typeof enVal === "string" && enVal.trim()) return enVal;
      }
      const val = record[field];
      return typeof val === "string" ? val : undefined;
    },
    [locale],
  );

  const value = useMemo(
    () => ({ locale, setLocale, toggleLocale, t, pickLocalized, isLoading }),
    [locale, setLocale, toggleLocale, t, pickLocalized, isLoading],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};
